/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { I18nMessage, useTranslations } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type {
  AdminDashUserResponse,
  CommunityType,
  MethodVariables,
} from "@packages/sdk";
import {
  AccountType,
  existingStudentForm,
  newStudentForm,
  regularMemberForm,
  RoleID,
  schoolMemberForm,
} from "@packages/sdk";
import { existingStudentValidation } from "@packages/sdk/src/lib/types/schema/community/members/adminDashMembersSchema";
import type { ThemeType } from "@packages/ui";
import {
  Button,
  CloseIcon,
  createModal,
  cssMediaQueryMinWidthTablet,
  FullscreenModalModal,
  IconButton,
  spacing,
  Text,
  useAlert,
  useConfirm,
  useModal,
} from "@packages/ui";
import * as RadixUIDialog from "@radix-ui/react-dialog";
import { useCallback, useMemo, useState } from "react";

import { renderUnknownError } from "../../../lib/utils/renderUnknownError";
import { SingleMemberForm } from "./SingleMemberForm";
import { StickyModalButtons } from "./StickyModalButtons";

const modalStyles: Interpolation = {
  height: "max-content",
  maxHeight: 760,
  maxWidth: 480,
};

const viewportStyles: Interpolation = {
  maxHeight: "initial",
  [cssMediaQueryMinWidthTablet]: {
    maxHeight: 760,
    padding: `${spacing.xl} ${spacing.xl} ${spacing.m} ${spacing.xl}`,
  },
};

const headerStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.m,
  justifyContent: "space-between",
  marginBottom: spacing.l,
  width: "100%",
};

const deleteButtonStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  color: theme.semanticColors.errorError,
});

export const MemberModal = createModal<{
  user: Partial<AdminDashUserResponse>;
  initialState?: "view" | "edit";
  communityType: CommunityType;
  onSubmit?: (
    values: MethodVariables<
      "/communities/{community_id}/admin/users/{user_id}",
      "PUT"
    >["body"],
  ) => Promise<void> | void;
  onDelete?: () => Promise<void> | void;
  onAdminChange?: () => Promise<void> | void;
  onRemove?: () => Promise<void> | void;
  onClose?: () => void;
  titleOverride?: string;
  closeOnDone?: boolean;
  hideRoleSelect?: boolean;
  eventProperties?: Record<string, string>;
}>(
  ({
    user,
    initialState = "view",
    communityType,
    onSubmit,
    onDelete,
    onAdminChange,
    onClose,
    onRemove,
    titleOverride,
    closeOnDone = false,
    hideRoleSelect,
    eventProperties = {},
  }) => {
    const { track } = useAnalytics();
    const modal = useModal();
    const t = useTranslations();
    const deleteConfirm = useConfirm({
      buttonFormat: "vertical-reverse",
      cancelButton: (handleConfirm, ...props) => (
        <Button
          isFullWidth
          onClick={handleConfirm}
          size="l"
          variant="neutral"
          {...props}
        >
          <I18nMessage message="admin_dash_cancel" />
        </Button>
      ),
      iconButtonProps: {
        variant: "transparentNeutral",
        size: "s",
      },
      modalProps: {
        contentCSS: [modalStyles],
      },
      primaryButton: (handleConfirm, ...props) => (
        <Button
          isFullWidth
          onClick={handleConfirm}
          size="l"
          css={deleteButtonStyles}
          variant="neutral"
          {...props}
        >
          <I18nMessage message="admin_dash_delete" />
        </Button>
      ),
      showCloseButton: true,
      size: "m",
    });
    const [state, setState] = useState<"view" | "edit">(initialState);
    const alert = useAlert();
    const [userData, setUserData] =
      useState<Partial<AdminDashUserResponse>>(user);

    const handleOpenChange = (open: boolean) => {
      if (open) return modal.show();
      else
        track({
          event: HallowAnalyticsEvent.TappedExitOnEditMember,
          properties: {
            screen_name: "members_edit_modal",
          },
        });
      modal.resolve(false);
      modal.remove();
      onClose?.();
    };

    const canEditAndDelete = communityType === "school";
    const canPromoteAndDemote = communityType !== "school";
    const canRemoveFromGroup = communityType !== "school";

    const handleSave = useCallback(
      (values: Partial<AdminDashUserResponse>) => {
        const isAdd = titleOverride?.includes("Add");
        track({
          event: isAdd
            ? HallowAnalyticsEvent.TappedAddMember
            : HallowAnalyticsEvent.TappedSaveOnEditMember,
          properties: {
            ...eventProperties,
            screen_name: isAdd ? "member_creation_modal" : "members_edit_modal",
            role: values.role_id === RoleID.Student ? "student" : "staff",
            auth_type: !!values.email ? "email" : "username",
            account_type:
              values.account_type === AccountType.Kid ? "kids" : "standard",
          },
        });
      },
      [titleOverride],
    );

    const schema = useMemo(() => {
      if (communityType !== "school") return regularMemberForm;
      // double-equals (instead of triple) is intentional because we're dealing with HTML forms
      // that coerce numbers to strings
      if (userData?.id) {
        if (userData.role_id == RoleID.Student) return existingStudentForm;
      }
      if (userData?.role_id == RoleID.Student) return newStudentForm;
      return schoolMemberForm;
    }, [userData?.id, userData?.role_id, communityType]);
    const validationSchema = useMemo(() => {
      if (communityType !== "school") return undefined;
      if (userData?.id && userData.role_id == RoleID.Student)
        return existingStudentValidation;
      return undefined;
    }, [userData?.id, userData?.role_id, communityType]);

    return (
      <FullscreenModalModal
        open={modal.visible}
        onOpenChange={handleOpenChange}
        fullScreenModalProps={{ showCloseButton: false }}
        modalProps={{ contentCSS: [modalStyles], showClose: false }}
        scrollAreaProps={{ viewportCSS: viewportStyles }}
      >
        <div css={headerStyles}>
          <RadixUIDialog.Title asChild>
            <Text as="h2" size="s" type="headline">
              {titleOverride ??
                t(
                  state === "edit"
                    ? "admin_dash_edit_member"
                    : "admin_dash_member_profile",
                )}
            </Text>
          </RadixUIDialog.Title>
          <RadixUIDialog.Close asChild>
            <IconButton
              icon={<CloseIcon />}
              onClick={() => handleOpenChange(false)}
              size="s"
              variant="transparentNeutral"
            />
          </RadixUIDialog.Close>
        </div>
        <SingleMemberForm
          key={userData?.id ?? "new-user"}
          onBeforeSubmit={(data) => setUserData({ ...userData, ...data })}
          onSubmit={async (values) => {
            setUserData({ ...userData, ...values });
            handleSave(values);
            for (const key of Object.keys(values)) {
              if (typeof values[key] === "string" && values[key].length === 0) {
                delete values[key];
              }
            }
            try {
              await onSubmit(
                values as MethodVariables<
                  "/communities/{community_id}/admin/users/{user_id}",
                  "PUT"
                >["body"],
              );
              if (closeOnDone) {
                modal.resolve();
                modal.remove();
              } else {
                setState("view");
              }
            } catch (e) {
              await alert.show({
                title: t("general_word_error"),
                description: renderUnknownError(e, t),
              });
            }
          }}
          schema={schema}
          validationSchema={validationSchema}
          user={userData}
          image_url={userData?.image_url}
          readOnly={state !== "edit"}
          hideRoleSelect={hideRoleSelect}
        >
          {(state === "edit" ||
            canEditAndDelete ||
            canPromoteAndDemote ||
            canRemoveFromGroup) && (
            <StickyModalButtons>
              {state === "edit" ? (
                <>
                  <Button type="submit" isFullWidth size="l">
                    {t("admin_dash_save")}
                  </Button>
                  <Button
                    isFullWidth
                    size="l"
                    variant="neutral"
                    onClick={() => {
                      if (closeOnDone) {
                        track({
                          event: HallowAnalyticsEvent.TappedExitOnEditMember,
                          properties: {
                            screen_name: "members_edit_modal",
                          },
                        });
                        modal.resolve();
                        modal.remove();
                      } else {
                        setState("view");
                      }
                    }}
                  >
                    {t("admin_dash_cancel")}
                  </Button>
                </>
              ) : (
                <>
                  {canEditAndDelete && (
                    <>
                      <Button
                        isFullWidth
                        size="l"
                        onClick={() => setState("edit")}
                      >
                        {t("settings_edit_profile")}
                      </Button>
                      <Button
                        isFullWidth
                        size="l"
                        variant="transparentPrimary"
                        css={deleteButtonStyles}
                        onClick={async () => {
                          const confirm = await deleteConfirm.show({
                            title: t("admin_dash_delete_confirmation_title"),
                            description: t(
                              "admin_dash_delete_confirmation_subtitle",
                            ),
                          });
                          if (confirm) {
                            track({
                              event: HallowAnalyticsEvent.TappedDeleteMember,
                            });
                            await onDelete();
                          }
                        }}
                      >
                        {t("settings_delete_account_delete")}
                      </Button>
                    </>
                  )}
                  {canPromoteAndDemote && (
                    <Button
                      isFullWidth
                      size="l"
                      onClick={onAdminChange}
                      type="button"
                    >
                      {user.is_admin
                        ? t("community_member_option_assign_as_member")
                        : t("community_member_option_assign_as_admin")}
                    </Button>
                  )}
                  {canRemoveFromGroup && (
                    <Button
                      isFullWidth
                      size="l"
                      type="button"
                      variant="transparentPrimary"
                      css={deleteButtonStyles}
                      onClick={onRemove}
                    >
                      {t("community_member_option_remove")}
                    </Button>
                  )}
                </>
              )}
            </StickyModalButtons>
          )}
        </SingleMemberForm>
      </FullscreenModalModal>
    );
  },
);
